import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import "./styles.css";

import { useParams } from "react-router-dom";
import api from "@helpers/api";

import { useCommunity } from "@contexts/CommunityContext";

import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";

import { useScreen } from "Screen/ScreenContext";

import Player from "./components/Player";
import EmptyStage from "./components/EmptyStage";
import PlaylistOutro from "./components/PlaylistOutro";
import { getPlaylistEntryContent } from "./helpers/playlistEntry";
import { theaterStore } from "./components/Player/stores/preferences";
import { applyMockCaptions } from "./components/Player/mockCaptions";
import { UserAvatar } from "@components/Community";
import { isProfileLinkable, profileLinkProps } from "@helpers/profile";
import { CommunityBanner } from "@components/Community";
import {
  AddRounded,
  ArrowDownward,
  ArrowDropDownRounded,
  CheckCircleRounded,
  CommentRounded,
  KeyboardArrowDownRounded,
  SendRounded,
  SlowMotionVideoRounded,
  MoreVertRounded,
  DeleteRounded,
  ReplyRounded,
  AttachmentRounded,
  AttachFileRounded,
  LockRounded,
  ShareRounded,
  KeyboardArrowRightRounded,
} from "@mui/icons-material";
import Popper from "@fragments/Popper";
import parser from "@helpers/parser";
import { useAuth } from "@contexts/AuthContext";
import { sendMessageTo } from "@contexts/ChatContext";
import { CircularProgress, Divider, Skeleton } from "@mui/material";

import { Button, Text } from "@fragments";

import ManageContentModal from "@modals/Content/ManageContent";
import GenerateVideoScriptModal from "@modals/Content/GenerateVideoScript";

import videoHelpers from "@helpers/video.js";

import { ReactComponent as PremiumIcon } from "@assets/custom-icons/premium.svg";
import { IconPlaystationCircle } from "@tabler/icons-react";

// Componente de loading com 5 itens usando Material Skeleton
function CardLoading() {
  return (
    <div className="card-loading-container">
      {Array.from({ length: 5 }).map((_, index) => (
        <div key={index} className="card-loading-item">
          <div className="card-loading-header">
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              className="card-loading-avatar"
            />
            <div className="card-loading-user-info">
              <Skeleton
                variant="text"
                width={120}
                height={20}
                className="card-loading-username"
              />
              <Skeleton
                variant="text"
                width={80}
                height={16}
                className="card-loading-timestamp"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Content() {
  let { slug, section: sectionId, content: contentId } = useParams();

  const { loading: communityLoading } = useCommunity();
  const { setFullWidth, setBreadcrumb } = useScreen();

  useEffect(() => {
    setFullWidth(true);
    return () => setFullWidth(false);
  }, [setFullWidth]);

  const [lesson, setLesson] = useState({});
  const [course, setCourse] = useState({});

  // Bumped after the creator saves the lesson from this page (a video was
  // added), so the same fetch below runs again for the current route.
  const [reloadTick, setReloadTick] = useState(0);

  const REF_manageContentModal = useRef();
  const REF_generateScriptModal = useRef();

  // Every route change fetches the content, then refreshes its section so the
  // playlist panel sees current watched flags. The cancel flag drops responses
  // from a navigation that has since been superseded — without it a slow
  // request could land after a newer one and put the page on the wrong video.
  useEffect(() => {
    if (communityLoading || !contentId) return;

    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get(`content/${contentId}`);
        if (cancelled) return;
        // `?mock_captions=<variant>`: decorate the video with the captions
        // shape the backend will ship (card 868kk6pe2) — no-op otherwise.
        setLesson({ ...data, video: applyMockCaptions(data.video) });

        const { data: section } = await api.get(`section/${data.section}`);
        if (cancelled) return;
        setCourse({ ...section });
      } catch (e) {
        if (!cancelled) console.log(e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [contentId, communityLoading, reloadTick]);

  // The playlist the current lesson lives in, resolved against the section
  // payload. This is the single source both the player's prev/next controls
  // and the side panel derive from, so they can never disagree.
  const currentPlaylist = useMemo(
    () => course?.content?.find((p) => p.id == lesson?.playlist) ?? null,
    [course?.content, lesson?.playlist],
  );

  // Playlist neighbors for autoplay and the prev/next controls.
  const { prevContent, nextContent } = useMemo(() => {
    if (lesson?.single) return { prevContent: null, nextContent: null };
    const contents = currentPlaylist?.contents;
    if (!contents?.length || !lesson?.id)
      return { prevContent: null, nextContent: null };
    const idx = contents.findIndex((c) => c.id == lesson.id);
    if (idx < 0) return { prevContent: null, nextContent: null };
    return {
      prevContent: idx > 0 ? contents[idx - 1] : null,
      nextContent: idx < contents.length - 1 ? contents[idx + 1] : null,
    };
  }, [currentPlaylist, lesson?.id, lesson?.single]);

  // Single navigation path for every content jump on this page: the player's
  // prev/next + autoplay, the panel rows, the playlist selector and the
  // next-playlist card. Contents nested in the section payload may omit their
  // section id, so fall back to the one in the route.
  const handleNavigateContent = (content) => {
    if (!content?.id) return;
    window.navigate(
      `/${slug}/section/${content.section ?? sectionId}/${content.id}`,
    );
  };

  // "Modo teatro" is toggled from inside the player; the page follows it to
  // rearrange the grid (stage full-width, playlist beside the description).
  const [theaterMode, setTheaterMode] = useState(theaterStore.read);
  useEffect(() => theaterStore.subscribe(setTheaterMode), []);

  const orientation =
    lesson?.video?.aspect_ratio < 1 ? "vertical" : "horizontal";

  // While the lesson has no video, creators get the player with its controls
  // inert and a card with the two next actions (upload it, or get a script
  // to record it) floating on the stage. Same role check the player uses.
  const canManage = ["admin", "owner", "creator"].includes(lesson?.role);
  const showEmptyStage = !!lesson?.id && !lesson?.video?.src && canManage;

  function handleOpenAddVideo() {
    REF_manageContentModal.current?.open(
      {
        ...lesson,
        section: lesson.section ?? sectionId,
        community: lesson.community,
      },
      // The editor calls back with the saved content, or with nothing when
      // the creator deleted it — then this page has nothing left to show.
      (saved) => {
        if (!saved) {
          window.navigate(`/${slug}/section/${lesson.section ?? sectionId}`);
          return;
        }
        setReloadTick((tick) => tick + 1);
      },
    );
  }

  function handleOpenGenerateScript() {
    REF_generateScriptModal.current?.open({ id: lesson.id, name: lesson.name });
  }

  return (
    <div className="content-page pb-8">
      <div
        className={"content-container mt-3" + (theaterMode ? " theater" : "")}
      >
        <div className={"content-stage " + orientation}>
          <Player
            info={lesson}
            key={lesson?.id}
            mode={orientation}
            prevContent={prevContent}
            nextContent={nextContent}
            onNavigate={handleNavigateContent}
            placeholder={
              showEmptyStage ? (
                <EmptyStage
                  onAddVideo={handleOpenAddVideo}
                  onGenerateScript={handleOpenGenerateScript}
                />
              ) : null
            }
          />
        </div>
        <div className="content-info-description mt-1">
          <Text.h2 weight={500} className="content-title mb-1">
            {lesson?.name}
          </Text.h2>
          <CreatorInfo lesson={lesson} />
          <Description lesson={lesson} />
          <CommentsSection lesson={lesson} />
        </div>
        <ContentSuggestions
          module={currentPlaylist}
          course={course}
          lesson={lesson}
          onOpenContent={handleNavigateContent}
        />
      </div>

      {canManage && (
        <>
          <ManageContentModal modalRef={REF_manageContentModal} />
          <GenerateVideoScriptModal modalRef={REF_generateScriptModal} />
        </>
      )}
    </div>
  );
}

function CreatorInfo({ lesson }) {
  const { t } = useTranslation("content");
  const { community, communityManagement } = useCommunity();
  const currentCommunity = community?.get(lesson?.community);

  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(
    ["creator", "owner", "member"].includes(currentCommunity?.role),
  );

  useEffect(() => {
    setSubscribed(
      ["creator", "owner", "member"].includes(currentCommunity?.role),
    );
  }, [currentCommunity?.role]);

  async function handleSubscribeClick() {
    setLoading(true);
    await community.subscribe(currentCommunity.id, () => {
      setSubscribed(true);
    });
    setLoading(false);
  }

  async function handleUnsubscribeClick() {
    setLoading(true);
    await community.unsubscribe(currentCommunity.id, () => {
      setSubscribed(false);
    });
    setLoading(false);
  }

  function handleShareContentClick() {
    window.handleOpenShareContentModal?.({
      title: lesson?.name || t("share.modalTitle"),
      link: window.location.href,
    });
  }

  if (!lesson?.creator) return null;

  return (
    <div className="creator-info">
      <div className="video-actions-row">
        <UserAvatar
          user={lesson?.creator}
          size={44}
          displayName={true}
          displayNickname={true}
        />

        <KeyboardArrowRightRounded style={{ transform: "translateX(-3px)" }} />

        <div className="community-block">
          <CommunityBanner banner={currentCommunity?.banner} size={38} />

          <div className="community-name-block">
            <Text.h5 weight={500}>{currentCommunity?.name}</Text.h5>
            <Text.small contrast={6}>
              {t("creator.members", {
                total: currentCommunity?.members?.total || 0,
              })}
            </Text.small>
          </div>

          {subscribed && currentCommunity?.role !== "creator" && (
            <Button
              tooltip={t("creator.manageSubscription")}
              size="small"
              color="white"
              loading={loading}
              endIcon={<ArrowDropDownRounded />}
              onClick={handleUnsubscribeClick}
            >
              {t("creator.subscribed")}
            </Button>
          )}

          {!subscribed && currentCommunity?.role !== "creator" && (
            <Button
              tooltip={t("creator.subscribe")}
              size="small"
              color="white"
              loading={loading}
              onClick={handleSubscribeClick}
            >
              {t("creator.subscribe")}
            </Button>
          )}
        </div>
      </div>
      <div className="video-actions-row">
        <Button
          tooltip={t("share.tooltip")}
          size="medium"
          color="overlay"
          endIcon={<ShareRounded />}
          onClick={handleShareContentClick}
        >
          {t("common:actions.share")}
        </Button>
      </div>
    </div>
  );
}

function Attachments({ lesson }) {
  const { t } = useTranslation("content");

  if (!lesson?.attachments?.length) return null;

  function ensureValidUrl(url) {
    if (!url) return "#";

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    return `https://${url}`;
  }

  return (
    <div className="attachments-container">
      <Text.h4
        className="mt-3 mb-1"
        value={t("attachments.title")}
        icon={<AttachmentRounded />}
      />
      <div className="attachments-list">
        {lesson?.attachments?.map((attachment, index) => (
          <a
            key={index}
            href={
              lesson?.availability?.available
                ? ensureValidUrl(attachment.url)
                : "#"
            }
            target={lesson?.availability?.available ? "_blank" : ""}
            rel="noopener noreferrer"
            className={
              "pressable attachment-item bsf-8 " +
              (lesson?.availability?.available ? "" : " disabled")
            }
          >
            {!lesson?.availability?.available && (
              <LockRounded style={{ fontSize: 18 }} />
            )}
            <span className="attachment-text">
              {attachment.text || attachment.name || attachment.title}
            </span>
            <AttachFileRounded style={{ fontSize: 18 }} />
          </a>
        ))}
      </div>
    </div>
  );
}

function Description({ lesson }) {
  const { t } = useTranslation("content");

  return (
    <div className="content-description-block mt-2">
      <small className="pb-2">
        {t("description.meta", {
          views: lesson?.views || 0,
          ago: parser.date.ago(lesson?.timestamps?.created),
        })}
      </small>

      {lesson?.description?.split("\n").map((line, index) => (
        <h5 className="content-description mt-1" key={index}>
          {line}
        </h5>
      ))}

      <Attachments lesson={lesson} />
    </div>
  );
}

/**
 * ContentSuggestions — the side panel: playlist selector, the rows of the
 * current playlist and the outro card (next playlist / section done).
 *
 * The playlist shown is always derived from the lesson (`module`), never held
 * as its own copy — so the selector, the row list, the outro card and the
 * player's prev/next all read the same section data. The only local state is
 * a short-lived optimistic override while a playlist change is in flight.
 *
 * @param {object}   module         Playlist the current lesson belongs to (null while loading).
 * @param {object}   course         The `section/:id` payload.
 * @param {object}   lesson         The current content payload.
 * @param {function} onOpenContent  Navigates to a content.
 */
function ContentSuggestions({ module, course, lesson, onOpenContent }) {
  const { t } = useTranslation("content");
  const popperRef = useRef();

  // Optimistic state, cleared the moment the route change lands a new lesson:
  // the playlist picked in the selector / outro card, and the row clicked.
  const [pendingPlaylist, setPendingPlaylist] = useState(null);
  const [pendingContentId, setPendingContentId] = useState(null);

  useEffect(() => {
    setPendingPlaylist(null);
    setPendingContentId(null);
  }, [lesson?.id]);

  const info =
    pendingPlaylist && pendingPlaylist.id != module?.id
      ? pendingPlaylist
      : module;
  const selectedId = pendingContentId ?? lesson?.id;

  function handleOpenContent(content) {
    if (!content?.id) return;
    setPendingContentId(content.id);
    onOpenContent(content);
  }

  // Every playlist change — from the selector or the next-playlist card —
  // lands on the same video: the first one not watched yet, else the first
  // in the playlist. The route change remounts the player (which autoplays)
  // and the screen body scrolls back to the top on its own.
  function handleOpenPlaylist(playlist) {
    if (!playlist?.id || playlist.id == info?.id) return;

    const entry = getPlaylistEntryContent(playlist);
    if (!entry) return;

    setPendingPlaylist(playlist);
    handleOpenContent(entry);
  }

  function handleModuleSelect(event) {
    if (!course?.content) return;

    // Only playlists with videos are selectable (singles are not playlists).
    const modules = course.content.filter(
      (item) => !item.single && item.contents?.length,
    );

    const options = modules.map((playlist) => ({
      id: playlist.id,
      label: playlist.name,
      onClick: () => {
        popperRef.current.close();
        handleOpenPlaylist(playlist);
      },
    }));

    popperRef.current.open(options, "bottom", event.currentTarget);
  }

  return (
    <div className="content-sugestions">
      <CurrentModule info={info} lesson={lesson} onClick={handleModuleSelect} />

      {!lesson?.single && (
        <h3 className="content-suggestions-title mt-3 mb-1 bold">
          {t("suggestions.moreInPlaylist")}
        </h3>
      )}

      {!lesson?.single &&
        info?.contents?.map((content) => (
          <ContentCard
            key={content.id}
            content={content}
            active={selectedId === content.id}
            onOpen={handleOpenContent}
          />
        ))}

      {!info && !lesson?.single && <CardLoading />}

      {/* Closes the panel: jump to the next playlist, or wrap the section up. */}
      {!lesson?.single && info && (
        <PlaylistOutro
          playlist={info}
          course={course}
          onOpenPlaylist={handleOpenPlaylist}
        />
      )}

      {!lesson?.single && <Popper controlRef={popperRef} />}
    </div>
  );
}

function CurrentModule({ info, lesson, onClick }) {
  const { t } = useTranslation("content");

  return (
    <div
      className={"current-module " + (!info?.banner ? "pl-2" : "")}
      onClick={onClick}
    >
      {info?.banner ? (
        <img src={info?.banner} alt="" className="current-module-banner" />
      ) : null}
      <div className="current-module-title">
        <Text.caption
          weight={400}
          contrast={5}
          left={<IconPlaystationCircle />}
        >
          {lesson?.single
            ? t("suggestions.singleVideo")
            : t("suggestions.currentPlaylist")}{" "}
          •{" "}
          {lesson?.single
            ? t("suggestions.oneVideo")
            : t("suggestions.videosCount", {
                total: info?.contents?.length || 0,
              })}
        </Text.caption>
        <Text.h3 weight={500} contrast={8}>
          {lesson?.single ? lesson?.name : info?.name}
        </Text.h3>
      </div>
      <KeyboardArrowDownRounded className="current-module-arrow" />
    </div>
  );
}

function ContentCard({ content, active, onOpen }) {
  const { t } = useTranslation("content");
  const [thumb, setThumb] = useState(content?.banner || null);
  const isHorizontal = content?.video?.aspect_ratio > 1;

  // Generate a thumbnail when the content has no banner.
  useEffect(() => {
    if (content?.banner) {
      setThumb(content.banner);
      return;
    }
    if (!content?.video?.src) return;

    let cancelled = false;
    videoHelpers
      .generateThumbFromVideo(content.video.src)
      .then((generated) => {
        if (!cancelled) setThumb(generated);
      })
      .catch(() => {
        /* no thumbnail — the row still shows title + creator */
      });

    return () => {
      cancelled = true;
    };
  }, [content?.banner, content?.video?.src]);

  return (
    <div
      className={
        "content-card mt-1 " +
        (isHorizontal ? "horizontal" : "vertical") +
        (active ? " active" : "")
      }
      onClick={() => onOpen(content)}
    >
      <div
        className="content-card-banner"
        style={{
          aspectRatio: content?.aspect_ratio,
          maxWidth: isHorizontal ? "unset" : "60px",
        }}
      >
        {thumb && <img src={thumb} alt={content?.name} />}

        <PlayCircleOutlineRoundedIcon
          className="content-card-play-icon"
          color="white"
        />

        {content.watched && (
          <CheckCircleRounded className="content-card-watched-icon" />
        )}
      </div>

      <div className="content-card-info">
        <Text.h5 weight={400} maxLines={2} contrast={8}>
          {content?.name}
        </Text.h5>

        <div className="content-creation-info">
          <UserAvatar user={content?.creator} size={19} displayName={true} />
        </div>
      </div>

      {!content?.availability?.available && (
        <span className="content-card-premium-icon-container">
          <small>{t("suggestions.premium")}</small>
          <PremiumIcon className="content-card-premium-icon" />
        </span>
      )}
    </div>
  );
}

function CommentsSection({ lesson }) {
  const { t } = useTranslation("content");
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const popperRef = useRef();

  const loadComments = async () => {
    if (!lesson?.id) return;

    setLoadingComments(true);
    try {
      const { data } = await api.get(`content/comment/${lesson.id}`);

      console.log("comments", data);

      setComments(data || []);
    } catch (error) {
      console.error("Error loading comments:", error);
      window.setSnackbar(t("comments.loadError"), "error");
    } finally {
      setLoadingComments(false);
    }
  };

  useEffect(() => {
    loadComments();
  }, [lesson?.id]);

  const handleCommentAction = (event, comment, isChild = false) => {
    const options = [];

    // Reply option (only for root comments, not sub-comments)
    if (!isChild) {
      options.push({
        id: "reply",
        label: t("comments.reply"),
        icon: <ReplyRounded style={{ fontSize: 16 }} />,
        onClick: () => {
          setReplyingTo(replyingTo === comment.id ? null : comment.id);
          popperRef.current.close();
        },
      });
    }

    // Send message option (only if comment owner is different from logged user)
    if (comment.user?.id !== user?.id) {
      options.push({
        id: "message",
        label: t("comments.sendMessage"),
        icon: <SendRounded style={{ fontSize: 16 }} />,
        onClick: () => {
          sendMessageTo(comment.user);
          popperRef.current.close();
        },
      });
    }

    // Delete option (only if comment owner is the logged user)
    if (comment.user?.id === user?.id) {
      options.push({
        id: "delete",
        label: t("common:actions.delete"),
        icon: <DeleteRounded style={{ fontSize: 16 }} />,
        variant: "delete",
        onClick: async () => {
          try {
            await api.delete(`content/comment/${comment.id}`);
            window.setSnackbar(t("comments.deleted"), "success");
            loadComments();
          } catch (error) {
            console.error("Error deleting comment:", error);
            window.setSnackbar(t("comments.deleteError"), "error");
          }
          popperRef.current.close();
        },
      });
    }

    popperRef.current.open(options, "bottom", event.currentTarget);
  };

  function AddComment({ replyTo = null, onCancel = null }) {
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const textareaRef = useRef(null);

    const adjustTextareaHeight = () => {
      const textarea = textareaRef.current;
      if (textarea) {
        // Reset height to auto to get the natural height
        textarea.style.height = "auto";

        // Calculate the new height, but ensure it's at least the minimum
        const newHeight = Math.max(textarea.scrollHeight, 42);
        const finalHeight = Math.min(newHeight, 120);

        textarea.style.height = finalHeight + "px";

        // Only show scrollbar if content exceeds max height
        if (textarea.scrollHeight > 120) {
          textarea.style.overflowY = "auto";
        } else {
          textarea.style.overflowY = "hidden";
        }
      }
    };

    const handleInputChange = (e) => {
      setInputValue(e.target.value);
      adjustTextareaHeight();
    };

    const handleSubmit = async () => {
      const trimmedValue = inputValue.trim();

      // Validation
      if (trimmedValue.length < 3) {
        window.setSnackbar(t("comments.minLength"), "error");
        return;
      }

      if (trimmedValue.length > 500) {
        window.setSnackbar(t("comments.maxLength"), "error");
        return;
      }

      setIsLoading(true);

      try {
        const payload = {
          content: lesson.id,
          text: trimmedValue,
        };

        // Add answer_of parameter if this is a reply
        if (replyTo) {
          payload.answer_of = replyTo;
        }

        await api.post("content/comment", payload);

        setInputValue("");
        adjustTextareaHeight();
        window.setSnackbar(t("comments.published"), "success");

        // Reload comments after successful post
        loadComments();

        // If this was a reply, close the reply form
        if (replyTo && onCancel) {
          onCancel();
        }
      } catch (error) {
        console.error("Error posting comment:", error);
        window.setSnackbar(t("comments.publishError"), "error");
      } finally {
        setIsLoading(false);
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };

    // Focus the textarea when component mounts (for replies)
    useEffect(() => {
      if (replyTo && textareaRef.current) {
        textareaRef.current.focus();
      }
    }, [replyTo]);

    // Adjust height when inputValue changes
    useEffect(() => {
      adjustTextareaHeight();
    }, [inputValue]);

    return (
      <div className="add-comment-container mt-2 mb-3">
        <UserAvatar user={user} size={32} link={false} />

        <div className="add-comment-input-container">
          <textarea
            ref={textareaRef}
            placeholder={
              replyTo
                ? t("comments.replyPlaceholder")
                : t("comments.addPlaceholder")
            }
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            rows={1}
            className="comment-textarea"
          />
        </div>

        {inputValue.length > 3 ? (
          <button
            className="add-comment-submit"
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading && <CircularProgress size={16} />}
            <span>
              {replyTo ? t("comments.reply") : t("common:actions.publish")}
            </span>
            <SendRounded style={{ fontSize: 16 }} />
          </button>
        ) : (
          <div className="add-comment-plus">
            <AddRounded />
          </div>
        )}
      </div>
    );
  }

  function CommentsList() {
    if (loadingComments) {
      return (
        <div className="comments-loading">
          <CircularProgress size={24} style={{ color: "white" }} />
          <span>{t("comments.loading")}</span>
        </div>
      );
    }

    if (comments.length === 0) {
      return (
        <div className="comments-empty">
          <span>{t("comments.empty")}</span>
        </div>
      );
    }

    const renderComment = (comment, isChild = false) => (
      <div key={comment.id} className={isChild ? "comment-child" : ""}>
        <div className="comment-item">
          <UserAvatar user={comment.user} size={32} />
          <div className="comment-content">
            <div className="comment-header">
              <span
                className={`comment-author bold${isProfileLinkable(comment.user) ? " comment-author--link" : ""}`}
                {...profileLinkProps(comment.user)}
              >
                {comment.user?.username || t("common:entities.user")}
              </span>
              <span className="comment-date">
                •{"  "} {parser.date.ago(comment.timestamps?.created)}
              </span>

              <span
                className="pressable comment-menu-button"
                onClick={(e) => handleCommentAction(e, comment, isChild)}
              >
                <MoreVertRounded style={{ fontSize: 18 }} />
              </span>
            </div>
            <span className="comment-text">{comment.comment}</span>
            {!isChild && (
              <div className="comment-actions">
                <span
                  className="pressable answer-button"
                  onClick={() =>
                    setReplyingTo(replyingTo === comment.id ? null : comment.id)
                  }
                >
                  {t("comments.reply")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Reply form - only for root comments */}
        {!isChild && replyingTo === comment.id && (
          <div className="comment-reply">
            <AddComment
              replyTo={comment.id}
              onCancel={() => setReplyingTo(null)}
            />
          </div>
        )}

        {/* Render children comments */}
        {comment.children && comment.children.length > 0 && (
          <div className="comment-children">
            {comment.children.map((childComment) =>
              renderComment(childComment, true),
            )}
          </div>
        )}
      </div>
    );

    return (
      <div className="comments-list">
        {comments.map((comment, i) => (
          <>
            {i > 0 && <Divider flexItem className="comment-divider" />}
            {renderComment(comment)}
          </>
        ))}
      </div>
    );
  }

  return (
    <div className="comments-section">
      <Text.h4
        className="mt-3"
        weight={400}
        contrast={8}
        value={t("comments.title")}
        icon={<CommentRounded />}
      />
      <AddComment replyTo={null} />
      <CommentsList />
      <Popper controlRef={popperRef} />
    </div>
  );
}
